import Button from "components/common/Button";
import Input from "components/common/Input";
import api from "config/api";
import { useState } from "react";
import { toast } from "react-toastify";

type Props = {
  value: number;
  onSubmit: (v: string) => void;
};

const inititalData = {
  nameOnCard: "",
  cardNumber: "",
  expiryDate: "",
  cardCVC: "",
};

const formatExpiryInput = (value: string) => {
  const digits = value.replace(/\D/g, "").slice(0, 6);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
};

const Clover = ({ value, onSubmit }: Props) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(inititalData);
  const fieldSx = {
    width: "100%",
    "& .MuiOutlinedInput-root": {
      borderRadius: "14px",
      background: "linear-gradient(180deg, rgba(255,255,255,.92), rgba(248,250,252,.78))",
      boxShadow: "0 10px 22px rgba(15,23,42,.055)",
      "& fieldset": {
        borderColor: "rgba(15,23,42,.11)",
      },
      "&:hover fieldset": {
        borderColor: "rgba(15,23,42,.18)",
      },
      "&.Mui-focused": {
        boxShadow: "0 0 0 4px rgba(15,23,42,.06)",
      },
      "&.Mui-focused fieldset": {
        borderColor: "rgba(15,23,42,.24)",
      },
    },
    "& .MuiInputBase-input": {
      padding: "14px 16px",
      fontSize: "15px",
    },
    "& .MuiInputLabel-root": {
      color: "#667085",
      fontSize: "13px",
      fontWeight: 700,
    },
  };

  const handleSubmit = () => {
    toast.dismiss();
    if (!data.nameOnCard) {
      toast.error("Invalid Cardholder Name");
      return;
    }
    if (data.cardNumber.length !== 16) {
      toast.error("Invalid Card Number");
      return;
    }
    if (
      !data.expiryDate ||
      !/^(0[1-9]|1[0-2])\/([0-9]{4})$/.test(data.expiryDate)
    ) {
      toast.error("Invalid Expiry Date");
      return;
    }
    if (!/^\d+$/.test(data.cardCVC) || data.cardCVC.length !== 3) {
      toast.error("Invalid Card CVV");
      return;
    }
    setLoading(true);
    api
      .post("/api/Clover", { ...data, amount: value })
      .then(({ data }) => {
        onSubmit("CLOVER");
      })
      .catch(() => {
        toast.error("Something went wrong");
      })
      .finally(() => setLoading(false));
  };

  return (
    <div className="clover-form-shell mt-2 max-w-[760px]">
      <style>
        {`
          .clover-form-shell{
            border: 1px solid rgba(255,255,255,.76);
            border-radius: 20px;
            background:
              linear-gradient(180deg, rgba(255,255,255,.74), rgba(248,250,252,.58)),
              radial-gradient(circle at 15% 0%, rgba(255,255,255,.95), transparent 34%);
            box-shadow: 0 18px 44px rgba(15,23,42,.10), inset 0 1px 0 rgba(255,255,255,.76);
            padding: 16px;
            backdrop-filter: blur(16px);
          }
          .clover-field-grid{
            display:grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 12px;
          }
          .clover-field-full{
            grid-column: 1 / -1;
          }
          .clover-pay-now-btn{
            position: relative !important;
            overflow: hidden !important;
            z-index: 1;
            width: 100% !important;
            max-width: 360px !important;
            min-height: 50px !important;
            padding: 0 28px !important;
            border-radius: 12px !important;
            border: 1px solid #e8e8e8 !important;
            background: #e8e8e8 !important;
            color: #090909 !important;
            font-size: 15px !important;
            font-weight: 600 !important;
            letter-spacing: 0 !important;
            font-family: Arial, Helvetica, sans-serif !important;
            box-shadow: 6px 6px 12px #c5c5c5, -6px -6px 12px #ffffff !important;
            text-shadow: none;
            transition: all .2s ease-in !important;
          }
          .clover-pay-now-btn::before,
          .clover-pay-now-btn::after{
            content: "";
            position: absolute;
            display: block;
            border-radius: 50%;
            z-index: -1;
            transition: all .5s .1s cubic-bezier(.55, 0, .1, 1);
            background-color: rgba(var(--color-primary) / .08);
            pointer-events: none;
          }
          .clover-pay-now-btn::before{
            left: 50%;
            top: 100%;
            width: 140%;
            height: 180%;
            transform: translateX(-50%) scaleY(1) scaleX(1.25);
          }
          .clover-pay-now-btn::after{
            left: 55%;
            top: 180%;
            width: 160%;
            height: 190%;
            transform: translateX(-50%) scaleY(1) scaleX(1.45);
            background-color: rgb(var(--color-primary));
          }
          .clover-pay-now-btn:hover{
            color: rgb(var(--color-themeSelColor)) !important;
            border-color: rgb(var(--color-primary)) !important;
          }
          .clover-pay-now-btn:hover::before{
            top: -35%;
            background-color: rgb(var(--color-primary));
            transform: translateX(-50%) scaleY(1.3) scaleX(.8);
          }
          .clover-pay-now-btn:hover::after{
            top: -45%;
            background-color: rgb(var(--color-primary));
            transform: translateX(-50%) scaleY(1.3) scaleX(.8);
          }
          .clover-pay-now-btn:active{
            color: #666 !important;
            box-shadow: inset 4px 4px 12px #c5c5c5, inset -4px -4px 12px #ffffff !important;
          }
          .clover-pay-now-btn:disabled{
            cursor: not-allowed;
            background: #ececec !important;
            border-color: #ececec !important;
            color: rgba(16,24,40,.50) !important;
            box-shadow: none !important;
            text-shadow: none;
          }
          .clover-pay-now-btn:disabled::before,
          .clover-pay-now-btn:disabled::after{
            display: none;
          }
          @media (max-width: 640px){
            .clover-form-shell{
              padding: 12px;
            }
            .clover-field-grid{
              grid-template-columns: 1fr;
            }
            .clover-pay-now-btn{
              width: 100% !important;
            }
          }
        `}
      </style>
      <div className="clover-field-grid">
      <div className="clover-field-full">
      <Input
        size="small"
        type="text"
        required
        label="Cardholder Name"
        value={data.nameOnCard}
        sx={fieldSx}
        onValueChange={(nameOnCard) =>
          setData((e) => ({ ...e, nameOnCard: nameOnCard.toUpperCase() }))
        }
      />
      </div>
      <div className="clover-field-full">
      <Input
        size="small"
        type="text"
        required
        label="Card Number"
        placeholder="0000 0000 0000 0000"
        value={data.cardNumber}
        sx={fieldSx}
        onValueChange={(cardNumber) =>
          setData((e) => ({
            ...e,
            cardNumber: String(cardNumber || "").replace(/\D/g, "").slice(0, 16),
          }))
        }
      />
      </div>
      <div>
        <Input
          size="small"
          type="text"
          required
          className="flex-1"
          placeholder="MM/YYYY"
          label="Expiration"
          value={data.expiryDate}
          sx={fieldSx}
          onValueChange={(expiryDate) =>
            setData((e) => ({ ...e, expiryDate: formatExpiryInput(String(expiryDate || "")) }))
          }
        />
      </div>
      <div>
        <Input
          size="small"
          type="text"
          required
          label="CVV"
          className="flex-1"
          value={data.cardCVC}
          sx={fieldSx}
          onValueChange={(cardCVC) =>
            setData((e) => ({
              ...e,
              cardCVC: String(cardCVC || "").replace(/\D/g, "").slice(0, 3),
            }))
          }
        />
      </div>
      <p className="clover-field-full text-[12px] text-[#667085]">
        Expiration format: MM/YYYY
      </p>
      <div className="clover-field-full pt-1">
        <Button
          disabled={loading}
          onClick={handleSubmit}
          className="clover-pay-now-btn"
        >
          Pay Now
        </Button>
      </div>
      </div>
    </div>
  );
};

export default Clover;
